import {
  fontFaceSchema,
  parseProject,
  snapshotFont,
  styleSchema,
  uid,
  verifyFontBytes,
  type FontFaceDescriptor,
  type Project,
} from "@proof/core";

export interface FontLibraryRecord {
  face: FontFaceDescriptor;
  tenantId: string | null;
  system: boolean;
  origin?: "installed-baseline";
  status: "active" | "archived";
  createdAt: string;
}
export interface FontLibraryServices {
  listFonts(signal: AbortSignal): Promise<FontLibraryRecord[]>;
  loadFont(face: FontFaceDescriptor, signal: AbortSignal): Promise<Uint8Array>;
  uploadFont?(file: File, signal: AbortSignal): Promise<FontLibraryRecord>;
  archiveFont?(id: string, signal: AbortSignal): Promise<FontLibraryRecord>;
}
export interface FontLibraryArtifact {
  blob: Blob;
  filename: string;
  record: FontLibraryRecord;
}
export function snapshotFontLibraryRecords(value: unknown, tenantId: string) {
  if (!Array.isArray(value) || value.length > 10000)
    throw new TypeError("Invalid font catalog");
  const ids = new Set<string>();
  return value.map((input): FontLibraryRecord => {
    if (
      !input ||
      typeof input !== "object" ||
      typeof input.system !== "boolean" ||
      (input.system ? input.tenantId !== null : input.tenantId !== tenantId) ||
      !["active", "archived"].includes(input.status) ||
      (input.origin !== undefined &&
        (input.origin !== "installed-baseline" || !input.system)) ||
      typeof input.createdAt !== "string" ||
      input.createdAt.length > 100 ||
      !Number.isFinite(Date.parse(input.createdAt))
    )
      throw new TypeError("Invalid or foreign font record");
    const face = fontFaceSchema.parse(input.face);
    if ((!input.system && face.bytes > 8000000) || ids.has(face.id))
      throw new TypeError("Invalid or duplicate font record");
    ids.add(face.id);
    return {
      face,
      tenantId: input.tenantId,
      system: input.system,
      ...(input.origin ? { origin: input.origin } : {}),
      status: input.status,
      createdAt: input.createdAt,
    };
  });
}
export function bindFontLibraryServices(
  input: FontLibraryServices,
): FontLibraryServices {
  for (const key of ["listFonts", "loadFont"] as const)
    if (!input || typeof input[key] !== "function")
      throw new TypeError(`Provide a ${key} callback`);
  for (const key of ["uploadFont", "archiveFont"] as const)
    if (input[key] !== undefined && typeof input[key] !== "function")
      throw new TypeError(`Provide an ${key} callback`);
  return {
    listFonts: (signal) => input.listFonts.call(input, signal),
    loadFont: async (descriptor, signal) => {
      const face = fontFaceSchema.parse(descriptor);
      signal.throwIfAborted();
      const value = await input.loadFont.call(
        input,
        structuredClone(face),
        signal,
      );
      signal.throwIfAborted();
      if (
        !ArrayBuffer.isView(value) ||
        Object.prototype.toString.call(value) !== "[object Uint8Array]" ||
        value.byteLength > 64000000
      )
        throw new Error("Invalid managed font bytes");
      const bytes = new Uint8Array(
        value.buffer,
        value.byteOffset,
        value.byteLength,
      ).slice();
      verifyFontBytes(face, bytes);
      return bytes;
    },
    ...(input.uploadFont && {
      uploadFont: (file: File, signal: AbortSignal) => {
        if (
          file.size < 20 ||
          file.size > 8000000 ||
          !/\.(ttf|otf|woff2?)$/i.test(file.name)
        )
          throw new TypeError(
            "Choose a static TTF, OTF, WOFF or WOFF2 file between 20 bytes and 8 MB",
          );
        signal.throwIfAborted();
        return input.uploadFont!.call(input, file, signal);
      },
    }),
    ...(input.archiveFont && {
      archiveFont: (id: string, signal: AbortSignal) => {
        if (!/^font_[a-f0-9]{64}$/.test(id))
          throw new TypeError("Invalid font identity");
        signal.throwIfAborted();
        return input.archiveFont!.call(input, id, signal);
      },
    }),
  };
}
export function createFontLibraryStyle(
  project: Project,
  selected: FontLibraryRecord,
  catalog: FontLibraryRecord[],
) {
  const records = snapshotFontLibraryRecords(catalog, project.tenantId);
  const record = records.find(
    (record) =>
      record.face.id === selected.face.id && record.status === "active",
  );
  if (
    !record ||
    JSON.stringify(record) !==
      JSON.stringify(
        snapshotFontLibraryRecords([selected], project.tenantId)[0],
      )
  )
    throw new TypeError("Select an active catalog face");
  const next = snapshotFont(
    parseProject(structuredClone(project)),
    record.face,
    records
      .filter((record) => record.status === "active")
      .map((record) => record.face),
  );
  return parseProject({
    ...next,
    styles: [
      ...next.styles,
      styleSchema.parse({
        id: uid("style"),
        name: record.face.name,
        fontFamily: record.face.family,
        fontFaceId: record.face.id,
        fontWeight: record.face.weight,
      }),
    ],
  });
}
export function downloadFontLibraryFace(artifact: FontLibraryArtifact) {
  const url = URL.createObjectURL(artifact.blob),
    anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = artifact.filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
